import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useCartVersion } from 'restful/tickets/queries/useCartVersion';
import {
  ticketsCartQuery,
  type TicketsCartQueryData,
} from 'restful/tickets/queries/useTicketsCart';
import { useTicketingCartId, useTicketingEventId } from 'restful/tickets/utils';
import { restCall } from 'services/api/restful/client';
import { RestApiError } from 'services/api/restful/errors';
import type {
  CartItemInput,
  CartUpdateInput,
} from 'services/api/restful/generated/nofomoRestApi.schemas';
import { updateCart } from 'services/api/restful/generated/tickets/tickets';

type ReplaceCartVariables = {
  items: Array<CartItemInput>;
};

export const useTicketsReplaceCart = () => {
  const { cartId, setCartId } = useTicketingCartId();
  const { eventId } = useTicketingEventId();
  const version = useCartVersion();
  const queryClient = useQueryClient();

  const mutation = useMutation<
    TicketsCartQueryData,
    RestApiError,
    ReplaceCartVariables
  >({
    mutationKey: ['tickets', 'cart', cartId] as const,
    mutationFn: async ({ items }) => {
      const input: CartUpdateInput = {
        version,
        action: { action: 'ReplaceItems', items },
      };

      const { data } = await restCall(() => updateCart(eventId, cartId, input));
      return { cart: data };
    },
    onMutate: () => {
      const queryKey = ticketsCartQuery({ eventId, cartId }).queryKey;
      return queryClient.cancelQueries({ queryKey });
    },
    onSuccess: ({ cart }) => {
      if (cart?.id) {
        queryClient.setQueryData(
          ticketsCartQuery({ eventId, cartId: cart.id }).queryKey,
          { cart },
        );
        setCartId(cart.id);
      }
    },
  });

  return {
    replaceCart: mutation.mutate,
    isLoading: mutation.isPending && !mutation.isPaused,
    error: mutation.error,
  };
};
